import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { groupBy } from 'lodash'
import { Check, ChevronsUpDown, Pencil, Star, X } from 'lucide-react'
import { Fragment, ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useContrastedBackground } from './Card'

export interface SelectProps {
  setValue: (value: string | undefined) => void
  value?: string
  options: {
    label: ReactNode | string
    value: string
    sortLabel?: string
    customOptionFn?: (isSelected: boolean) => ReactNode
    category?: string
    premium?: boolean
    description?: string
    className?: string
  }[]
  disabled?: boolean
  onClick?: (e: React.MouseEvent<unknown>) => void
  isSearchable?: boolean
  isClearable?: boolean
  inModal?: boolean
  placeholder?: ReactNode
  className?: string
  minimumSearchLength?: number
  emptyMessage?: string
  separateCategories?: boolean
  sortDirection?: 'asc' | 'desc'
  optionWidth?: 'matchTrigger' | 'fit'
  iconOnly?: boolean
  hideIcon?: boolean
  footerMessage?: ReactNode
  style?: React.CSSProperties
}

const Select = ({
  setValue,
  value,
  options,
  disabled,
  onClick,
  className,
  style,
  isSearchable,
  isClearable,
  placeholder,
  emptyMessage,
  inModal = false,
  minimumSearchLength = 0,
  separateCategories = false,
  sortDirection = 'asc',
  optionWidth = 'matchTrigger',
  iconOnly = false,
  hideIcon = false,
  footerMessage,
}: SelectProps) => {
  const { t } = useTranslation()
  const background = useContrastedBackground()
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')

  const selectedOption = options?.find((o) => o.value === value)

  const groupedOptions = useMemo(
    () =>
      groupBy(
        (options || []).sort((a, b) =>
          sortDirection === 'asc'
            ? `${a.category}-${a.sortLabel ?? a.label}`.localeCompare(
                `${b.category}-${b.sortLabel ?? b.label}`,
              )
            : `${b.category}-${b.sortLabel ?? b.label}`.localeCompare(
                `${a.category}-${a.sortLabel ?? a.label}`,
              ),
        ),
        'category',
      ),
    [options, sortDirection],
  )

  return (
    <Popover
      open={disabled ? false : open}
      onOpenChange={setOpen}
      modal={inModal}
    >
      <PopoverTrigger asChild>
        {iconOnly ? (
          <button
            type='button'
            role='combobox'
            aria-expanded={open}
            className={cn(
              'hover:text-accent-foreground transition-colors',
              disabled && 'cursor-not-allowed opacity-70',
              className,
            )}
            onClick={onClick}
          >
            <Pencil size={20} />
          </button>
        ) : (
          <Button
            variant='outline'
            role='combobox'
            aria-expanded={open}
            className={`w-full justify-between ${background} border overflow-hidden ${className} ${
              disabled ? 'cursor-not-allowed opacity-70' : ''
            }`}
            onClick={onClick}
            style={style}
          >
            <span
              className={`${
                value !== undefined ? '' : 'text-muted-foreground'
              } overflow-hidden`}
            >
              <span
                className={`flex items-center gap-2 ${
                  selectedOption?.className ?? ''
                }`}
              >
                {!selectedOption?.category || separateCategories ? null : (
                  <Badge variant='outline'>{selectedOption.category}</Badge>
                )}
                <span
                  className={`truncate ${
                    selectedOption?.label ? '' : 'text-muted-foreground'
                  }`}
                >
                  {selectedOption?.label ?? placeholder ?? 'Select option'}
                </span>
              </span>
            </span>
            {value !== undefined && value !== null && isClearable ? (
              <X
                className='ml-2 mr-[-0.3rem] opacity-50 hover:opacity-100 transition-opacity p-1'
                onClick={(e) => {
                  e.preventDefault()
                  setValue(undefined)
                }}
              />
            ) : hideIcon ? null : (
              <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 text-muted-foreground' />
            )}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          'p-1',
          optionWidth === 'matchTrigger' && !iconOnly
            ? 'w-(--radix-popover-trigger-width)'
            : 'w-fit',
        )}
      >
        <Command
          filter={(value, search) => {
            if (value.toLowerCase().includes(search.toLowerCase())) return 1
            return 0
          }}
        >
          {isSearchable ? (
            <CommandInput
              placeholder={'Search'}
              value={term}
              onValueChange={setTerm}
            />
          ) : null}

          <CommandList className='h-full' onClick={onClick}>
            {(term.length >= minimumSearchLength || !options.length) && (
              <CommandEmpty>
                {emptyMessage ?? t('shared.no-results')}
              </CommandEmpty>
            )}
            {Object.entries(groupedOptions).map(([category, options]) => {
              const optionElements = options.map((option) => (
                <CommandItem
                  key={option.value as string}
                  value={option.value}
                  className='flex-col gap-1 items-start'
                  onSelect={() => {
                    setValue(option.value)
                    setOpen(false)
                  }}
                >
                  {option.customOptionFn ? (
                    option.customOptionFn(option.value === value)
                  ) : (
                    <>
                      <div
                        className={`flex w-full items-center gap-2 ${
                          option.className ?? ''
                        }`}
                      >
                        {option.category && !separateCategories ? (
                          <Badge variant='outline'>{option.category}</Badge>
                        ) : null}

                        <span className='grow'>{option.label}</span>

                        <Check
                          size={16}
                          className={cn(
                            'ml-auto min-w-4',
                            option.value === value
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />

                        {option.premium ? (
                          <Star
                            width={12}
                            className='text-subscription-gold mr-1'
                          />
                        ) : null}
                      </div>
                      {option.description ? (
                        <div className='text-xs text-muted-foreground'>
                          {option.description}
                        </div>
                      ) : null}
                    </>
                  )}
                </CommandItem>
              ))

              return (
                <Fragment key={category}>
                  {separateCategories && category !== 'undefined' ? (
                    <CommandGroup heading={category}>
                      {optionElements}
                    </CommandGroup>
                  ) : (
                    optionElements
                  )}
                </Fragment>
              )
            })}
          </CommandList>

          {footerMessage ? (
            <div className='p-2 border-t'>{footerMessage}</div>
          ) : null}
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default Select
